'use client';

/**
 * SDOH Assessment Form Component
 *
 * Editable form for creating and updating SDOH assessments.
 * Supports:
 * - Domain score editing with visual sliders
 * - Need identification and management
 * - Severity and risk level assignment
 * - Service recommendations
 *
 * Integrates with Migration 122: Service Referral SDOH Integration
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Home,
  Utensils,
  Car,
  DollarSign,
  GraduationCap,
  Users,
  Heart,
  Shield,
  AlertTriangle,
  CheckCircle,
  Plus,
  Trash2,
  Loader2,
  Save,
  X,
} from 'lucide-react';
import {
  SDOHDomainCode,
  SDOHSeverity,
  SDOHAssessmentData,
  SDOHNeedData,
} from '@/services/api/serviceReferrals';

// ============================================================================
// CONSTANTS
// ============================================================================

const SDOH_DOMAINS: Array<{
  code: SDOHDomainCode;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  { code: 'HOUSING', name: 'Housing', icon: Home, description: 'Housing stability, affordability, safety' },
  { code: 'FOOD_NUTRITION', name: 'Food & Nutrition', icon: Utensils, description: 'Food security, nutrition access' },
  { code: 'TRANSPORTATION', name: 'Transportation', icon: Car, description: 'Transportation access for medical/work' },
  { code: 'ECONOMIC_STABILITY', name: 'Economic Stability', icon: DollarSign, description: 'Financial stress, employment stability' },
  { code: 'EDUCATION_EMPLOYMENT', name: 'Education & Employment', icon: GraduationCap, description: 'Education, job training, employment' },
  { code: 'SOCIAL_SUPPORT', name: 'Social Support', icon: Users, description: 'Social isolation, family support' },
  { code: 'HEALTH_BEHAVIORS', name: 'Health Behaviors', icon: Heart, description: 'Tobacco, substance use, physical activity' },
  { code: 'SAFETY', name: 'Safety', icon: Shield, description: 'Personal safety, domestic violence' },
];

const SEVERITY_OPTIONS: Array<{ value: SDOHSeverity; label: string; color: string }> = [
  { value: 'low', label: 'Low', color: 'bg-green-500' },
  { value: 'moderate', label: 'Moderate', color: 'bg-yellow-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
];

const ASSESSMENT_TYPES = [
  { value: 'screening', label: 'Screening' },
  { value: 'full_assessment', label: 'Full Assessment' },
  { value: 're_assessment', label: 'Re-Assessment' },
  { value: 'follow_up', label: 'Follow-Up' },
];

const ASSESSMENT_TOOLS = [
  { value: 'PRAPARE', label: 'PRAPARE' },
  { value: 'AHC-HRSN', label: 'AHC-HRSN' },
  { value: 'custom', label: 'Custom Screening' },
  { value: 'other', label: 'Other' },
];

// ============================================================================
// TYPES
// ============================================================================

interface DomainScores {
  housing: number;
  food: number;
  transportation: number;
  economic: number;
  education_employment: number;
  social_support: number;
  health_behaviors: number;
  safety: number;
}

interface NeedInput {
  id: string;
  domain_code: SDOHDomainCode;
  description: string;
  severity: SDOHSeverity;
  is_primary: boolean;
}

interface SDOHAssessmentFormProps {
  referralId: number;
  memberId?: number;
  existingAssessment?: SDOHAssessmentData;
  existingNeeds?: SDOHNeedData[];
  onSave?: (assessment: SDOHAssessmentData, needs: NeedInput[]) => Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
  className?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getScoreColor(score: number): string {
  if (score <= 25) return 'bg-green-500';
  if (score <= 50) return 'bg-yellow-500';
  if (score <= 75) return 'bg-orange-500';
  return 'bg-red-500';
}

function calculateOverallScore(scores: DomainScores): number {
  const values = Object.values(scores);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function determineRiskLevel(score: number): SDOHSeverity {
  if (score <= 25) return 'low';
  if (score <= 50) return 'moderate';
  if (score <= 75) return 'high';
  return 'critical';
}

function getPriorityDomains(scores: DomainScores): SDOHDomainCode[] {
  const domainMap: Record<keyof DomainScores, SDOHDomainCode> = {
    housing: 'HOUSING',
    food: 'FOOD_NUTRITION',
    transportation: 'TRANSPORTATION',
    economic: 'ECONOMIC_STABILITY',
    education_employment: 'EDUCATION_EMPLOYMENT',
    social_support: 'SOCIAL_SUPPORT',
    health_behaviors: 'HEALTH_BEHAVIORS',
    safety: 'SAFETY',
  };

  return Object.entries(scores)
    .filter(([_, score]) => score >= 50)
    .sort(([_, a], [__, b]) => b - a)
    .map(([key]) => domainMap[key as keyof DomainScores]);
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SDOHAssessmentForm({
  referralId,
  memberId,
  existingAssessment,
  existingNeeds = [],
  onSave,
  onCancel,
  readOnly = false,
  className = '',
}: SDOHAssessmentFormProps) {
  const { t } = useAppTranslation();

  // Form state
  const [assessmentType, setAssessmentType] = useState<string>(
    existingAssessment?.assessment_type || 'screening'
  );
  const [assessmentTool, setAssessmentTool] = useState<string>(
    existingAssessment?.assessment_tool || 'PRAPARE'
  );
  const [scores, setScores] = useState<DomainScores>({
    housing: existingAssessment?.housing_score || 0,
    food: existingAssessment?.food_score || 0,
    transportation: existingAssessment?.transportation_score || 0,
    economic: existingAssessment?.economic_score || 0,
    education_employment: existingAssessment?.education_employment_score || 0,
    social_support: existingAssessment?.social_support_score || 0,
    health_behaviors: existingAssessment?.health_behaviors_score || 0,
    safety: existingAssessment?.safety_score || 0,
  });
  const [needs, setNeeds] = useState<NeedInput[]>(
    existingNeeds.map((n, i) => ({
      id: `need-${n.id || i}`,
      domain_code: n.sdoh_domain_code,
      description: n.need_description || '',
      severity: n.severity,
      is_primary: n.is_primary,
    }))
  );
  const [notes, setNotes] = useState(existingAssessment?.notes || '');
  const [requiresFollowUp, setRequiresFollowUp] = useState(existingAssessment?.requires_follow_up || false);
  const [followUpDate, setFollowUpDate] = useState(existingAssessment?.follow_up_date || '');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculated values
  const overallScore = calculateOverallScore(scores);
  const riskLevel = determineRiskLevel(overallScore);
  const priorityDomains = getPriorityDomains(scores);

  const updateScore = (domain: keyof DomainScores, value: number) => {
    setScores((prev) => ({ ...prev, [domain]: value }));
  };

  const addNeed = () => {
    setNeeds((prev) => [
      ...prev,
      {
        id: `need-${Date.now()}`,
        domain_code: priorityDomains[0] || 'HOUSING',
        description: '',
        severity: 'moderate',
        is_primary: prev.length === 0,
      },
    ]);
  };

  const removeNeed = (id: string) => {
    setNeeds((prev) => {
      const filtered = prev.filter((n) => n.id !== id);
      // Ensure at least one is primary if any exist
      if (filtered.length > 0 && !filtered.some((n) => n.is_primary)) {
        filtered[0].is_primary = true;
      }
      return filtered;
    });
  };

  const updateNeed = (id: string, updates: Partial<NeedInput>) => {
    setNeeds((prev) =>
      prev.map((n) => {
        if (n.id !== id) {
          // If setting this one as primary, unset others
          if (updates.is_primary) {
            return { ...n, is_primary: false };
          }
          return n;
        }
        return { ...n, ...updates };
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const assessmentData: SDOHAssessmentData = {
        id: existingAssessment?.id || 0,
        assessment_type: assessmentType as any,
        assessment_tool: assessmentTool,
        assessed_at: new Date().toISOString(),
        overall_sdoh_score: overallScore,
        risk_level: riskLevel,
        priority_domains: priorityDomains,
        housing_score: scores.housing,
        food_score: scores.food,
        transportation_score: scores.transportation,
        economic_score: scores.economic,
        education_employment_score: scores.education_employment,
        social_support_score: scores.social_support,
        health_behaviors_score: scores.health_behaviors,
        safety_score: scores.safety,
        requires_follow_up: requiresFollowUp,
        follow_up_date: followUpDate || undefined,
        notes: notes || undefined,
      };

      await onSave?.(assessmentData, needs);
    } catch (err: any) {
      setError(err.message || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const domainScoreMapping: Record<SDOHDomainCode, keyof DomainScores> = {
    HOUSING: 'housing',
    FOOD_NUTRITION: 'food',
    TRANSPORTATION: 'transportation',
    ECONOMIC_STABILITY: 'economic',
    EDUCATION_EMPLOYMENT: 'education_employment',
    SOCIAL_SUPPORT: 'social_support',
    HEALTH_BEHAVIORS: 'health_behaviors',
    SAFETY: 'safety',
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              {t('serviceReferrals.sdoh.assessmentTitle', 'SDOH Assessment')}
            </CardTitle>
            <CardDescription>
              {existingAssessment ? t('serviceReferrals.sdoh.updateAssessment', 'Update') : t('serviceReferrals.sdoh.completeAssessment', 'Complete')} {t('serviceReferrals.sdoh.assessmentDesc', 'social determinants of health assessment')}
            </CardDescription>
          </div>
          <Badge
            className={`
              ${riskLevel === 'low' ? 'bg-green-100 text-green-700' : ''}
              ${riskLevel === 'moderate' ? 'bg-yellow-100 text-yellow-700' : ''}
              ${riskLevel === 'high' ? 'bg-orange-100 text-orange-700' : ''}
              ${riskLevel === 'critical' ? 'bg-red-100 text-red-700' : ''}
            `}
          >
            Overall: {overallScore}% - {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Risk
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Assessment Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('serviceReferrals.sdoh.assessmentType', 'Assessment Type')}</Label>
            <Select value={assessmentType} onValueChange={setAssessmentType} disabled={readOnly}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSESSMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('serviceReferrals.sdoh.assessmentTool', 'Assessment Tool')}</Label>
            <Select value={assessmentTool} onValueChange={setAssessmentTool} disabled={readOnly}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSESSMENT_TOOLS.map((tool) => (
                  <SelectItem key={tool.value} value={tool.value}>
                    {tool.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        {/* Domain Scores */}
        <div className="space-y-4">
          <Label className="text-base font-medium">{t('serviceReferrals.sdoh.domainRiskScores', 'Domain Risk Scores')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('serviceReferrals.sdoh.domainRiskScoresDesc', 'Rate each domain from 0 (no risk) to 100 (critical risk). Scores above 50 indicate priority domains.')}
          </p>

          <div className="space-y-4">
            {SDOH_DOMAINS.map((domain) => {
              const scoreKey = domainScoreMapping[domain.code];
              const score = scores[scoreKey];
              const Icon = domain.icon;
              const isPriority = score >= 50;

              return (
                <div
                  key={domain.code}
                  className={`p-4 rounded-lg border ${isPriority ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${isPriority ? 'bg-orange-100' : 'bg-gray-100'}`}>
                      <Icon className={`h-4 w-4 ${isPriority ? 'text-orange-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{domain.name}</span>
                        {isPriority && (
                          <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">
                            {t('serviceReferrals.sdoh.priority', 'Priority')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{domain.description}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-lg font-bold ${isPriority ? 'text-orange-600' : ''}`}>{score}%</span>
                    </div>
                  </div>

                  {!readOnly && (
                    <Slider
                      value={[score]}
                      onValueChange={([val]) => updateScore(scoreKey, val)}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                  )}

                  {readOnly && (
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getScoreColor(score)} transition-all duration-300`}
                        style={{ width: `${score}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Identified Needs */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">{t('serviceReferrals.sdoh.identifiedNeeds', 'Identified Needs')}</Label>
              <p className="text-sm text-muted-foreground">{t('serviceReferrals.sdoh.identifiedNeedsDesc', 'Document specific needs for each priority domain')}</p>
            </div>
            {!readOnly && (
              <Button type="button" variant="outline" size="sm" onClick={addNeed}>
                <Plus className="h-4 w-4 mr-1" />
                {t('serviceReferrals.sdoh.addNeed', 'Add Need')}
              </Button>
            )}
          </div>

          {needs.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground border rounded-lg">
              No specific needs documented yet.
              {!readOnly && ' Click "Add Need" to document identified needs.'}
            </div>
          ) : (
            <div className="space-y-3">
              {needs.map((need) => {
                const domain = SDOH_DOMAINS.find((d) => d.code === need.domain_code);
                const Icon = domain?.icon || AlertTriangle;

                return (
                  <div key={need.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-sm">{domain?.name || need.domain_code}</span>
                        {need.is_primary && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Primary</Badge>
                        )}
                      </div>
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeNeed(need.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {readOnly ? (
                      <div className="space-y-2">
                        <p className="text-sm">{need.description || 'No description'}</p>
                        <Badge
                          className={`
                            ${need.severity === 'low' ? 'bg-green-100 text-green-700' : ''}
                            ${need.severity === 'moderate' ? 'bg-yellow-100 text-yellow-700' : ''}
                            ${need.severity === 'high' ? 'bg-orange-100 text-orange-700' : ''}
                            ${need.severity === 'critical' ? 'bg-red-100 text-red-700' : ''}
                          `}
                        >
                          {need.severity}
                        </Badge>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">Domain</Label>
                          <Select
                            value={need.domain_code}
                            onValueChange={(v) => updateNeed(need.id, { domain_code: v as SDOHDomainCode })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SDOH_DOMAINS.map((d) => (
                                <SelectItem key={d.code} value={d.code}>
                                  {d.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Severity</Label>
                          <Select
                            value={need.severity}
                            onValueChange={(v) => updateNeed(need.id, { severity: v as SDOHSeverity })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SEVERITY_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${s.color}`} />
                                    {s.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label className="text-xs">Description</Label>
                          <Textarea
                            value={need.description}
                            onChange={(e) => updateNeed(need.id, { description: e.target.value })}
                            placeholder="Describe the specific need..."
                            rows={2}
                          />
                        </div>
                        <div className="col-span-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`primary-${need.id}`}
                              checked={need.is_primary}
                              onCheckedChange={(checked) => updateNeed(need.id, { is_primary: !!checked })}
                            />
                            <Label htmlFor={`primary-${need.id}`} className="text-sm">
                              Primary need for this referral
                            </Label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Separator />

        {/* Notes and Follow-up */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('serviceReferrals.sdoh.assessmentNotes', 'Assessment Notes')}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('serviceReferrals.sdoh.notesPlaceholder', 'Document observations, recommendations, and additional context...')}
              rows={4}
              disabled={readOnly}
            />
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="requires-follow-up"
                checked={requiresFollowUp}
                onCheckedChange={(checked) => setRequiresFollowUp(!!checked)}
                disabled={readOnly}
              />
              <Label htmlFor="requires-follow-up">{t('serviceReferrals.sdoh.requiresFollowUp', 'Requires follow-up assessment')}</Label>
            </div>

            {requiresFollowUp && (
              <div className="flex items-center gap-2">
                <Label className="text-sm">Follow-up date:</Label>
                <Input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="w-40"
                  disabled={readOnly}
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {!readOnly && (
        <CardFooter className="flex justify-between border-t pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            {t('serviceReferrals.common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('serviceReferrals.sdoh.saveAssessment', 'Save Assessment')}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

export default SDOHAssessmentForm;
